import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * 帳號健康看板：每個身分一列 —— 暱稱、服務、房間、狀態、最後活動，以及「重連」「刪除」。
 *
 * 狀態完全由 `GET /api/sessions` 現有欄位推出來，不新增後端契約：
 *   - `online === true`                        → 在線
 *   - `rateLimited` / `cooldownUntil` 未過期 / `lastError` 提到 429·限流 → 限流（後端有帶才判斷得到）
 *   - 離線但 10 分鐘內還有活動                → 休眠（叫得醒，重連即可）
 *   - 其餘（含從沒活動過）                    → 斷線
 *
 * 「重連」= 重打 join（`POST /api/sessions/:id/join`）喚醒 DO 並重新進房；刪除是兩段式，
 * 第一次點只變成「確定刪除」，不跳 confirm 對話框（瀏覽器 confirm 在多帳號連點時很煩）。
 */
import { useEffect, useMemo, useState } from 'react';
import { resolveDataState } from '../api/data-state';
import { DataStateNotice } from './DataStateNotice';
/** 離線但還在這個時間內有活動 → 休眠；超過就當斷線。 */
export const IDLE_WINDOW_MS = 10 * 60_000;
const LIMITED_PATTERN = /429|限流|cooldown|too many/i;
const timeFormat = new Intl.DateTimeFormat('zh-TW', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
});
const dateTimeFormat = new Intl.DateTimeFormat('zh-TW', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
});
/** 最後活動時間：今天只顯示時分，其他日子帶日期；沒有紀錄就 `—`。 */
export function formatLastActivity(ts, now = Date.now()) {
    if (typeof ts !== 'number' || !Number.isFinite(ts))
        return '—';
    const date = new Date(ts);
    const today = new Date(now);
    const sameDay = date.getFullYear() === today.getFullYear() &&
        date.getMonth() === today.getMonth() &&
        date.getDate() === today.getDate();
    return (sameDay ? timeFormat : dateTimeFormat).format(date);
}
/** 離線多久了（人話）：`剛斷線`／`12 分鐘`／`3 小時`／`2 天`。 */
export function formatIdleGap(ts, now = Date.now()) {
    if (typeof ts !== 'number' || !Number.isFinite(ts))
        return '沒有活動紀錄';
    const gap = Math.max(0, now - ts);
    if (gap < 60_000)
        return '剛斷線';
    const minutes = Math.floor(gap / 60_000);
    if (minutes < 60)
        return `${minutes} 分鐘`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24)
        return `${hours} 小時`;
    return `${Math.floor(hours / 24)} 天`;
}
/** 純函式：一個身分的健康狀態（可測、可重用）。 */
export function healthStatusOf(account, now = Date.now()) {
    const cooldownActive = typeof account.cooldownUntil === 'number' && account.cooldownUntil > now;
    const errored = LIMITED_PATTERN.test(account.lastError ?? '');
    if (account.rateLimited === true || cooldownActive || errored) {
        return { tone: 'limited', label: '限流', reason: '站方登入限流；等冷卻結束再重連，重連會再被擋一次' };
    }
    if (account.online) {
        return {
            tone: 'online',
            label: '在線',
            reason: account.room ? `已進房 ${account.room}` : '已連線、尚未進房',
        };
    }
    const last = typeof account.lastActivityAt === 'number' ? account.lastActivityAt : null;
    if (last !== null && now - last < IDLE_WINDOW_MS) {
        return { tone: 'idle', label: '休眠', reason: '連線閒置；按重連就會重打 join 喚醒' };
    }
    return { tone: 'down', label: '斷線', reason: `已 ${formatIdleGap(last, now)} 沒活動；按重連喚醒` };
}
export function HealthPanel({ accounts, activeId = null, busy = false, loading = false, error = null, errorKind = null, loaded = true, onRetry = null, onReconnect, onDelete, onSelect, onRefresh, now, }) {
    /** 兩段式刪除：記住哪一個帳號在等確認（4 秒後自動收回）。 */
    const [confirmId, setConfirmId] = useState(null);
    useEffect(() => {
        if (!confirmId)
            return;
        const timer = setTimeout(() => setConfirmId(null), 4000);
        return () => clearTimeout(timer);
    }, [confirmId]);
    const nowTs = now ?? Date.now();
    const rows = useMemo(() => accounts.map((account) => ({ account, status: healthStatusOf(account, nowTs) })), [accounts, nowTs]);
    const onlineCount = rows.filter((row) => row.status.tone === 'online').length;
    /** 四態：錯誤／載入中都先於「0 個身分」，避免把「讀不到」讀成「身分都沒了」。 */
    const dataState = resolveDataState({ loading, error, errorKind, count: accounts.length, loaded });
    const failed = dataState === 'offline' || dataState === 'unauthorized' || dataState === 'error';
    return (_jsxs("section", { className: "cui-panel cui-health", "data-testid": "health-panel", "data-state": dataState, "aria-label": "\u5E33\u865F\u5065\u5EB7", children: [_jsxs("header", { className: "cui-panel-head", children: [_jsx("h2", { className: "cui-panel-title", children: "\u5E33\u865F\u5065\u5EB7" }), _jsx("span", { className: "cui-count", "data-testid": "health-count", children: loading ? '載入中…' : failed ? '—' : `${onlineCount}/${accounts.length} 在線` }), onRefresh ? (_jsx("button", { type: "button", className: "cui-icon-btn", onClick: onRefresh, title: "\u91CD\u65B0\u6574\u7406", "aria-label": "\u91CD\u65B0\u6574\u7406\u5E33\u865F\u72C0\u614B", "data-testid": "health-refresh", children: "\u21BB" })) : null] }), dataState !== 'ready' ? (_jsx(DataStateNotice, { state: dataState, message: error, onRetry: onRetry ?? onRefresh ?? null, emptyText: "\u9084\u6C92\u6709\u8EAB\u5206\uFF1B\u5148\u6309\u300C\uFF0B \u5EFA\u5E33\u865F\u300D", emptyHint: "\u5EFA\u597D\u8EAB\u5206\u5F8C\uFF0C\u9019\u88E1\u6703\u5217\u51FA\u6BCF\u4E00\u9846\u7684\u5728\u7DDA\uFF0F\u4F11\u7720\uFF0F\u9650\u6D41\u72C0\u614B\u3002", loadingText: "\u8B80\u53D6\u8EAB\u5206\u5065\u5EB7\u2026", testId: "health-state" })) : null, accounts.length > 0 ? (_jsx("ul", { className: "cui-list cui-health-list", children: rows.map(({ account, status }) => {
                    const confirming = confirmId === account.id;
                    return (_jsxs("li", { className: `cui-health-row is-${status.tone}${account.id === activeId ? ' is-active' : ''}`, "data-testid": "health-row", "data-status": status.tone, "data-account-id": account.id, children: [_jsxs("div", { className: "cui-health-main", children: [_jsxs("div", { className: "cui-health-top", children: [_jsx("button", { type: "button", className: "cui-health-nick", onClick: onSelect ? () => onSelect(account.id) : undefined, disabled: !onSelect, title: onSelect ? '切到這個身分的對話' : undefined, "data-testid": "health-nickname", children: account.nickname || '（未命名）' }), _jsx("span", { className: `cui-badge cui-badge--${account.service}`, children: account.service }), _jsx("span", { className: `cui-health-pill is-${status.tone}`, "data-testid": "health-status", children: status.label })] }), _jsxs("div", { className: "cui-health-meta", children: [_jsxs("span", { children: ["\u623F ", _jsx("span", { className: "cui-mono", children: account.room ?? '—' })] }), _jsxs("span", { "data-testid": "health-last-activity", children: ["\u6700\u5F8C\u6D3B\u52D5 ", _jsx("span", { className: "cui-mono", children: formatLastActivity(account.lastActivityAt, nowTs) })] })] }), _jsx("p", { className: "cui-health-reason", children: status.reason })] }), _jsxs("div", { className: "cui-health-actions", children: [_jsx("button", { type: "button", className: "cui-btn cui-btn--sm", onClick: () => onReconnect(account), disabled: busy, "data-testid": "health-reconnect", title: "\u91CD\u6253 join \u559A\u9192\u9019\u500B\u8EAB\u5206", children: "\u91CD\u9023" }), _jsx("button", { type: "button", className: `cui-btn cui-btn--sm${confirming ? ' cui-btn--danger' : ' cui-btn--ghost'}`, onClick: () => {
                                            if (!confirming) {
                                                setConfirmId(account.id);
                                                return;
                                            }
                                            setConfirmId(null);
                                            onDelete(account);
                                        }, disabled: busy, "data-testid": "health-delete", "data-confirming": confirming, children: confirming ? '確定刪除' : '刪除' })] })] }, account.id));
                }) })) : null] }));
}
//# sourceMappingURL=HealthPanel.js.map